/* 
    Uploaded file helper function used in:
    Recorder.vue 
    Upload.vue 
    Editor.vue (for Pro users to upload recorded audio from instant sessions)

    The functionality for all the above require uploading to MP server (the uploaded file actually is uploaded to S3,
        but all the MP server calls scaffold the necessary entities for a session)
    
    
    The uploadAudio method:
    creates in mp-server:
    -session record
    -upload record
    -multipart upload URL

    Then PUTs BLOB/Audio to S3
*/

import EventEmitter from "events";
export const eventEmitter = new EventEmitter();
import { RECORDING_UPLOAD_STEP } from "./constants";
import {
  deleteRecordByIdFromIndexedDB,
  getAllRecordsFromIndexedDB,
  getRecordFromIndexedDB,
} from "../services/IndexedDB";
import {
  sliceBlobUsingArrayBuffer,
  supportSliceBlobUsingArrayBuffer,
} from "./helpers";

// create a session record in mp-server
export const createSession = async (uploadDataObject, store) => {
  if(!uploadDataObject.isRecoveredFile === true && !uploadDataObject.preventSessionCache === true)  {
  //Create a locally cached representation of this session in case it doesn't upload
  store.commit("user/addCachedSession", uploadDataObject)
  }

  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  let raw = JSON.stringify({
    title: uploadDataObject.recordingTitle,
    ...(uploadDataObject.localSessionId && { localSessionId: uploadDataObject.localSessionId }),
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  const response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL + "self/sessions",
    requestOptions
  );
  const data = await response.json();
  store.dispatch('itemList/addSessionToAllSessions', { item: data.data})
  return data.data.id;
};

// create an upload record in mp-server to retrieve uploadId to set up multipart upload URL in S3
export const createUpload = async ({
  sessionId,
  recordingDuration,
  multipart,
  fileExt,
  mimeType,
  uploadType,
  fileName,
  fileSize,
}, store, addUploadToSession = true) => {

  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  let raw = JSON.stringify({
    sessionId: sessionId,
    fileExtension: fileExt,
    type: uploadType,
    mimeType: mimeType,
    duration: recordingDuration,
    multipart: multipart,
    title: fileName,
    fileSize: fileSize,
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  const response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL + "self/uploads",
    requestOptions
  );

  const data = await response.json();
  if (addUploadToSession) {
    await store.dispatch('itemList/addUploadToSession', { sessionId: sessionId, upload: data.data})
  }
  return data.data.id;
};

// create URL for an S3 multipart upload, we use this to PUT BLOB to S3
export const createMultipartUploadURL = async ({uploadId, partNumber}) => {
  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  let raw = JSON.stringify({
    partNumber: partNumber,
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  const response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL +
      `self/uploads/${uploadId}/create-multipart-upload-url`,
    requestOptions
  );
  const data = await response.json();
  return data.data;
};

export const prepareS3Parts = async({recordingCacheFileBlob, canMultiUpload}) => {

  const CHUNK_SIZE = 1024 * 1024 * 5 //(5MB)
  let prepared = false
  let byteStart = 0
  let partNumber = 1
  let fileSizeInBytes = recordingCacheFileBlob.size

  let uploadQueueItems = []

  if (canMultiUpload) {
    while(!prepared) {
      if(fileSizeInBytes > byteStart) {
        const byteEnd = Math.min(fileSizeInBytes, byteStart + CHUNK_SIZE)
        uploadQueueItems.push({
          partNumber,
          position: byteStart,
          endPosition: byteEnd,
          length: byteEnd - byteStart,
          eTag: "",
          completed: false
        })
        partNumber = partNumber + 1
        byteStart = byteEnd
      } else {
        prepared = true
      }
    }
  } else {
    const byteEnd = fileSizeInBytes;
    uploadQueueItems.push({
      partNumber,
      position: byteStart,
      endPosition: byteEnd,
      length: byteEnd - byteStart,
      eTag: "",
      completed: false
    });
  }
  
  

  return uploadQueueItems
}

export const uploadToS3 = async (uploadDataObject, store) => {

  let {recordingCacheFileBlob, parts, uploadId, contentType, localSessionId, canMultiUpload} = uploadDataObject

  //Let's only work with parts that haven't been completed yet
  let unfinishedParts = parts.filter((part) => {
    return part.completed !== true
  })

  //Let's try and do 5 upload slices simultaneously
  const chunkSize = 5;
  for (let i = 0; i < unfinishedParts.length; i += chunkSize) {
    const chunk = unfinishedParts.slice(i, i + chunkSize);
    let promises = []
    for(let j = 0; j < chunk.length; j++) {
      promises.push(uploadQueueItem(recordingCacheFileBlob, chunk[j], uploadId, contentType, canMultiUpload))
    }

    const completedUploads = await Promise.all(promises)

    completedUploads.forEach((item) => {
      //Remember: partNumber index starts from 1!!
      parts[item.partNumber - 1]['eTag'] = item.etag
      parts[item.partNumber - 1]['completed'] = true

      let payload = { 
        sessionId: localSessionId,
        partNumber: item.partNumber,
        eTag: item.etag
      }

      //Remove this part from our cached upload queue
      if(uploadDataObject.preventSessionCache !== true) {
        store.commit("user/completedCachedUploadPart", payload)
      }
    })

    let uploadProgress = calculateProgress(uploadDataObject)
    eventEmitter.emit("uploadProgress", {localSessionId: localSessionId, progress: uploadProgress})
  }

  console.log("Upload done!")
  return parts
};

async function uploadQueueItem(recordingCacheFileBlob, part, uploadId, contentType, canMultiUpload) {
  return new Promise(function(resolve) {
    async function uploadQueueItemCallback() {
      let queueItem = part
      let start = queueItem.position
      let end = queueItem.endPosition - 1
      let recordingData;
      if (canMultiUpload) {
        recordingData = await sliceBlobUsingArrayBuffer(recordingCacheFileBlob, start, end)
      } else {
        recordingData = recordingCacheFileBlob
      }
      let multipartUploadURL = await createMultipartUploadURL({uploadId, partNumber: queueItem.partNumber})
      let response = await fetch(multipartUploadURL, {
        method: "PUT",
        body: recordingData,
        headers: new Headers({
          "Content-Type": contentType,
        }),
      });

      let etag = JSON.parse(response.headers.get("etag"));
      resolve({partNumber: queueItem.partNumber, etag: etag})
    }
    uploadQueueItemCallback()
  })
}

export const completeMultipartUpload = async ({uploadId, parts}) => {
  console.log("COMPLETING MULTIPART UPLOAD")
  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  const mappedParts = parts.map(item => {
    return {
        partNumber: item.partNumber,
        eTag: item.eTag,
    }
  })

  let raw = JSON.stringify({
    parts: mappedParts,
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  fetch(
    process.env.VUE_APP_MP_SERVER_API_URL +
      `self/uploads/${uploadId}/complete-multipart-upload`,
    requestOptions
  )
    .then((response) => response.text())
    .catch((error) => console.log("error", error));
};

export const tidyUploadCache = async ({localSessionId}, store) => {
  console.log("Removing media cache for session: " + localSessionId)
  //Remove the cached media file
  deleteRecordByIdFromIndexedDB(
    "AudioRecorderDB",
    "audioData",
    localSessionId,
    function(error, successMessage) {
    if (error) {
      console.error('Error deleting record:', error);
    } else {
      console.log(successMessage);
    }
  });
  store.commit("user/removeCachedSession", localSessionId);
}

export const uploadMedia = async (
  recordingTitle,
  recordingDuration,
  recordingDurationHMS,
  recordingCacheFileBlob,
  uploadType,
  isVideo,
  localSessionId,
  uploadDataObject,
  store
) => {
  if (!recordingCacheFileBlob) {
    const cacheSession = await getRecordFromIndexedDB(
      "AudioRecorderDB",
      "audioData",
      localSessionId,
    );
    recordingCacheFileBlob = cacheSession.data;
  }

  if (!uploadDataObject) {
    uploadDataObject = {
      currentStep: RECORDING_UPLOAD_STEP.CreateSession,

      createdAt: Date.now(),

      recordingTitle,
      recordingDuration,
      recordingDurationHMS,
      uploadType,
      localSessionId,

      multipart: true,
      fileExt: "webm",
      mimeType: isVideo ? "video/webm" : "audio/webm",

      recordingCacheFileBlob,
      contentType: isVideo ? "video/webm" : "audio/webm",
    };
  } else if (!uploadDataObject.recordingCacheFileBlob || uploadDataObject.recordingCacheFileBlob === null) {
    uploadDataObject.recordingCacheFileBlob = recordingCacheFileBlob;
  }

  const canMultiUpload = await supportSliceBlobUsingArrayBuffer(uploadDataObject.recordingCacheFileBlob);
  uploadDataObject.canMultiUpload = canMultiUpload;

  const uploadSequence = [
    {
      step: RECORDING_UPLOAD_STEP.CreateSession,
      errorMessage: "Failed to create session",
      func: createSession,
      returnKey: "sessionId",
    },
    {
      step: RECORDING_UPLOAD_STEP.CreateUpload,
      errorMessage: "Failed to create upload",
      func: createUpload,
      returnKey: "uploadId",
    },
    {
      step: RECORDING_UPLOAD_STEP.PrepareS3Parts,
      errorMessage: "Failed to prepare parts for upload",
      func: prepareS3Parts,
      returnKey: "parts",
    },
    {
      step: RECORDING_UPLOAD_STEP.UploadToS3,
      errorMessage: "Failed to upload the file to storage",
      func: uploadToS3,
      returnKey: "parts",
    },
    {
      step: RECORDING_UPLOAD_STEP.CompleteMultipartUpload,
      errorMessage: "Failed to complete multipart upload",
      func: completeMultipartUpload,
      returnKey: null,
    }
  ];

  while (
    uploadDataObject.currentStep <= RECORDING_UPLOAD_STEP.CompleteMultipartUpload
  ) {
    const {
      step, 
      errorMessage, 
      func, 
      returnKey,
    } = uploadSequence[uploadDataObject.currentStep];

    let uploadProgress = calculateProgress(uploadDataObject)
    eventEmitter.emit("uploadProgress", {localSessionId: uploadDataObject.localSessionId, progress: uploadProgress})
    store.commit("user/updateCachedSession", {
      sessionId: uploadDataObject.localSessionId, 
      patch: uploadDataObject
    })

    try {
      if(func) {
        const returnValue = await func(uploadDataObject, store);
        if (returnKey) {
          uploadDataObject[returnKey] = returnValue;
        }
      }
    } catch (error) {
      uploadDataObject.errorMessage = errorMessage;
      uploadDataObject.currentStep = step;
      console.log(error)
      return uploadDataObject;
    }

    uploadDataObject.currentStep += 1;

    uploadProgress = calculateProgress(uploadDataObject)
    eventEmitter.emit("uploadProgress", {localSessionId: uploadDataObject.localSessionId, progress: uploadProgress})
    store.commit("user/updateCachedSession", {
      sessionId: uploadDataObject.localSessionId, 
      patch: uploadDataObject
    })
  }

  const { bookmarkTimestamps } = uploadDataObject;
  if (bookmarkTimestamps) {
    await postBookmarkBatch(uploadDataObject);
  }

  tidyUploadCache(uploadDataObject, store)

  localStorage.setItem("latestRecordingUploadId", uploadDataObject.uploadId);
  localStorage.setItem("latestRecordingTitle", uploadDataObject.recordingTitle);
  localStorage.setItem("latestRecordingDurationMS", uploadDataObject.recordingDuration);
  localStorage.setItem("latestRecordingDurationHMS", uploadDataObject.recordingDurationHMS);

  uploadDataObject.errorMessage = undefined;

  return uploadDataObject;
}

export const recoverRecordings = async (store) => {
  const cachedSessions = await getAllRecordsFromIndexedDB(
    "AudioRecorderDB",
    "audioData",
  );
  for (const _cachedSession of cachedSessions) {
    let cachedSession = store.getters['user/cachedSessionByLocalSessionId'](_cachedSession.localSessionId)
    if(!cachedSession) {
      const bookmarkTimestampsRaw = localStorage.getItem(`bookmark-${_cachedSession.localSessionId}`);
      const bookmarkTimestamps = bookmarkTimestampsRaw ? JSON.parse(bookmarkTimestampsRaw) : undefined;
      localStorage.removeItem(`bookmark-${_cachedSession.localSessionId}`);

      store.commit("user/addCachedSession", {
        currentStep: RECORDING_UPLOAD_STEP.CreateSession,

        createdAt: Date.now(),

        recordingTitle: _cachedSession.sessionTitle !== ""  ? _cachedSession.sessionTitle : _cachedSession.birthTime,
        recordingDuration: 0,
        recordingDurationHMS: 0,
        uploadType: 'mp-desktop-recording',
        localSessionId: _cachedSession.id,
  
        multipart: true,
        fileExt: "webm",
        mimeType: "audio/webm",
  
        contentType: "audio/webm",

        isRecoveredFile: true,

        bookmarkTimestamps,
      })
    }
  }
}

export const calculateProgress = (uploadDataObject) => {
  //Each of the upload "steps" is going to constitute 5% of our upload progress
  //We have 5 steps
  let stepProgress = uploadDataObject.currentStep * 0.05

  //Then uploading the "parts" to S3 will constitute the rest
  let partsProgress = 0.0

  if(uploadDataObject.parts) {
    let totalParts = uploadDataObject.parts.length
    let uploadedParts = uploadDataObject.parts.filter((part) => {
      return part.completed === true
    }).length
    partsProgress = (uploadedParts / totalParts) * 0.75
  }

  return (stepProgress + partsProgress)
}

export const uploadDocument = async (
  fileName,
  mimeType,
  file,
  uploadType,
  sessionId,
  store
) => {
  eventEmitter.emit("uploadInProgress", true);

  const fileExtension = fileName.split(".").pop();
  eventEmitter.emit("updateProgressMessage", "Getting file ready...");

  let newSessionId;
  if (!sessionId) {
    newSessionId = await createSession({recordingTitle: fileName}, store);
  } else {
    newSessionId = sessionId;
  }

  const uploadId = await createUpload({
    sessionId: newSessionId,
    recordingDuration: null,
    multipart: false,
    fileExt: fileExtension,
    mimeType: mimeType,
    uploadType: uploadType,
    fileName,
    fileSize: Math.floor(file.size / 1000) // convert bytes to kb
  }, store, false);

  const uploadData = await postDocumentToS3(uploadId, file, fileName);
  eventEmitter.emit("updateProgressMessage", "Creating upload information...");
  eventEmitter.emit("uploadInProgress", false);
  eventEmitter.emit("uploadComplete");
  return uploadData;
};

export const postDocumentToS3 = async (
  uploadId,
  file,
  fileName,
) => {
  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");
  let raw = JSON.stringify({
    fileName: fileName,
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
  };

  let response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL +
      `self/uploads/${uploadId}/create-presigned-upload-url`,
    requestOptions
  );

  const responseData = await response.json();

  if (responseData.status !== "success") {
    console.log(responseData.status);
    return;
  }

  const { presignedPostUrl } = responseData.data;
  const formData = new FormData();
  Object.keys(presignedPostUrl.fields).forEach((key) => {
    formData.append(key, presignedPostUrl.fields[key]);
  });
  formData.append("file", file);

  myHeaders = new Headers();
  myHeaders.append("Access-Control-Allow-Origin", "*");
  myHeaders.append("Access-Control-Allow-Credentials", true);

  requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: formData,
  };

  response = await fetch(presignedPostUrl.url, requestOptions);

  if (response.status !== 204) {
    console.log(responseData.status);
    return;
  }

  myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  raw = JSON.stringify({
    s3Key: presignedPostUrl.fields.key,
    s3Bucket: presignedPostUrl.fields.bucket,
    status: "upload-complete",
  });

  requestOptions = {
    method: "PUT",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL + `self/uploads/${uploadId}`,
    requestOptions
  );

  const data = await response.json();
  return data.data;
};

export const postBookmarkBatch = async ({bookmarkTimestamps, uploadId}) => {
  let myHeaders = new Headers();
  myHeaders.append(
    "Authorization",
    `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
  );
  myHeaders.append("Content-Type", "application/json");

  let raw = JSON.stringify({
    timestamps: bookmarkTimestamps,
  });

  let requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  const response = await fetch(
    process.env.VUE_APP_MP_SERVER_API_URL +
      `self/uploads/${uploadId}/` +
      `bookmarks/batch`,
    requestOptions
  );
  const bookmarkResponse = await response.json();
  if (bookmarkResponse.status !== "success") {
    console.error(bookmarkResponse);
  }
}
